/**
 * Bluetooth feature configuration — build-time constants and per-environment
 * PSK accessor for on-site BLE provisioning.
 *
 * The PSK (pre-shared key) is a 16-byte secret shared across all OutdoorLink
 * units. Its value is never committed to source; it is supplied at deploy time
 * via the gitignored `src/config.js` as `BLUETOOTH_PSK`. A later task seeds
 * the PSK into secure on-device storage; this module is only the config layer.
 *
 * Contains:
 * - DEBUG_BLUETOOTH  — verbose BLE logging toggle
 * - getConfiguredPsk — returns the PSK hex string, throws if unset
 */

import { BLUETOOTH_PSK } from '@sophocles/config';

/** Enable verbose BLE logging. Set to false for production builds. */
export const DEBUG_BLUETOOTH = true;

/**
 * Returns the configured Bluetooth PSK as a hex string.
 *
 * Reads from the `BLUETOOTH_PSK` export in `src/config.js` (gitignored).
 * Throws at call time — not at import time — so the app can boot in
 * environments where BLE auth is not needed (web, non-provisioning flows).
 *
 * @returns {string} The PSK as a hex string.
 * @throws {Error} If BLUETOOTH_PSK is unset or empty in src/config.js.
 */
export function getConfiguredPsk() {
  if (!BLUETOOTH_PSK) {
    throw new Error(
      'BLUETOOTH_PSK is not configured. Add `export const BLUETOOTH_PSK = "<hex-string>";` to src/config.js.',
    );
  }
  return BLUETOOTH_PSK;
}
